import type { z } from 'zod'

import type { GameKey } from './games.js'
import type { Rng } from './rng.js'

/** How a game finished. */
export type GameResult = 'won' | 'lost' | 'abandoned'

/** Someone playing. Guests have an id and a name but no account. */
export interface Player {
  readonly id: string
  readonly displayName: string
  /** Null for a guest; results are only recorded for accounts. */
  readonly userId: string | null
}

/**
 * Why an action was refused.
 *
 * Refusals are values rather than exceptions. A bad message from one phone
 * must not throw inside a room that three other people are playing in.
 */
export type RejectionReason =
  | 'not-your-turn'
  | 'game-over'
  | 'invalid-action'
  | 'already-played'
  | 'unknown-player'
  /** Asked for too early — a pair is still showing and everybody gets to see it. */
  | 'too-soon'

export type Outcome<State> =
  | { readonly ok: true; readonly state: State }
  | { readonly ok: false; readonly reason: RejectionReason; readonly state: State }

export function accept<State>(state: State): Outcome<State> {
  return { ok: true, state }
}

/** Refusing returns the unchanged state, so a caller can always render something. */
export function reject<State>(state: State, reason: RejectionReason): Outcome<State> {
  return { ok: false, reason, state }
}

/** Everything a reducer is allowed to know about the outside world. */
export interface ActionContext {
  /** Who is acting. Checked against whose turn it is. */
  readonly playerId: string
  /** Injected rather than read from the clock, so time is testable too. */
  readonly now: number
}

/** Turn ownership, shared by every game that takes turns. */
export interface TurnState {
  readonly players: readonly Player[]
  /** Null in a solo game, or once the game is over. */
  readonly currentPlayerId: string | null
}

/**
 * What a finished game is worth recording.
 *
 * Note what is not here: a score. There are no points in this app, and no
 * bests or tables to climb. That is a deliberate choice rather than an
 * omission — the games already tell her how a round went, in the round's own
 * terms, and a number on top of that is a different kind of motivation from
 * the one worth encouraging.
 *
 * It also happens to remove a whole class of problem. A score is the one thing
 * in a browser-run game worth lying about, and most of the machinery around
 * issued rounds and witnessed moves exists because of it. Nothing here is worth
 * forging: making a round look worse is pointless, and making it look better
 * only means being asked harder questions next time.
 *
 * What is left is what an adult would actually want to know: which game, how
 * hard, how it ended, how long it took.
 */
export interface PlaySummary {
  readonly gameKey: GameKey
  readonly result: GameResult
  readonly durationMs: number
  /** Free-form per-game difficulty label, e.g. a reading band or a level. */
  readonly difficulty: string
}

/**
 * The contract every game implements.
 *
 * State is plain data and reducers are pure, which is what lets the identical
 * engine run in the browser for solo play and on the server, authoritatively,
 * for a room. If solo and multiplayer ever need different rule code here,
 * something has gone wrong — that divergence is the bug nobody finds until she
 * is playing with a cousin.
 */
export interface GameEngine<Config, State, Action> {
  readonly key: GameKey

  /**
   * Validates an untrusted action — from a socket message, or a URL.
   *
   * Parsing at the boundary means the reducer only ever sees a well-formed
   * action and can be written without defensive checks.
   */
  readonly actionSchema: z.ZodType<Action>

  createInitialState(config: Config, players: readonly Player[], rng: Rng): State

  /** Pure: same state and action always give the same outcome, and never mutates. */
  reduce(state: State, action: Action, context: ActionContext): Outcome<State>

  isTerminal(state: State): boolean

  summarise(state: State, endedAt: number): PlaySummary
}

/**
 * Parses an untrusted action and applies it.
 *
 * This is the only entry point the server and the UI should use — going
 * straight to reduce() would skip validation, which is exactly the mistake
 * that lets a malformed socket message reach a reducer.
 */
export function applyAction<Config, State, Action>(
  engine: GameEngine<Config, State, Action>,
  state: State,
  rawAction: unknown,
  context: ActionContext,
): Outcome<State> {
  if (engine.isTerminal(state)) return reject(state, 'game-over')

  const parsed = engine.actionSchema.safeParse(rawAction)
  if (!parsed.success) return reject(state, 'invalid-action')

  return engine.reduce(state, parsed.data, context)
}

/** Whose turn it is next, wrapping around. Null when there is nobody to play. */
export function nextPlayerId(turn: TurnState): string | null {
  if (turn.players.length === 0) return null
  if (turn.currentPlayerId === null) return turn.players[0]?.id ?? null

  const index = turn.players.findIndex((player) => player.id === turn.currentPlayerId)
  if (index === -1) return turn.players[0]?.id ?? null

  return turn.players[(index + 1) % turn.players.length]?.id ?? null
}

/** Whether this player is allowed to act right now. */
export function isPlayersTurn(turn: TurnState, playerId: string): boolean {
  // A solo game has nobody to wait for.
  if (turn.currentPlayerId === null) return turn.players.length <= 1
  return turn.currentPlayerId === playerId
}
